import {createActions} from 'redux-actions';
import fetchJson, {putJson, postJson, postForm, deleteJson, postFile} from '../fetchJson';
import uuidv4 from 'uuid/v4';
import handleError from '../handleError';
import reviewActions from './reviews.actions';
import {push} from 'connected-react-router';
import {sessionActions} from '../user';
import {uiActions} from '../chrome';
import ReactGA from 'react-ga';

const actions = createActions(
	'PROFILE_LOADING',
	'PROFILE_LOADED',
	'PROFILE_UNLOADED',
	'PROFILE_AFFILIATIONS_LOADED',
	'PROFILE_FAVOURITES_LOADED',
	'PROFILE_EDITING_FIELD',
	'PROFILE_UPDATE_ABOUT',
	'PROFILE_UPDATE_BRIEF',
	'PROFILE_UPDATE_NAME',
	'PROFILE_UPDATE_PROFESSION',
	'PROFILE_UPDATE_CATEGORY',
	'PROFILE_UPDATE_LOCATION',
	'PROFILE_COMMIT_CONTACTS',
	'PROFILE_EDIT_CONTACTS',
	'PROFILE_CHANGE_EDIT_CONTACTS',
	'PROFILE_CANCEL_CONTACTS',
	'PROFILE_DRAG_OVER_BACKGROUND_IMAGE',
	'PROFILE_DRAG_OUT_BACKGROUND_IMAGE',
	'PROFILE_UPDATING_BACKGROUND_IMAGE',
	'PROFILE_UPDATING_BACKGROUND_IMAGE_PROGRESS',
	'PROFILE_UPDATE_BACKGROUND_IMAGE',
	'PROFILE_DRAG_OVER_AVATAR_IMAGE',
	'PROFILE_DRAG_OUT_AVATAR_IMAGE',
	'PROFILE_UPDATING_AVATAR_IMAGE',
	'PROFILE_UPDATING_AVATAR_IMAGE_PROGRESS',
	'PROFILE_UPDATE_AVATAR_IMAGE',
	'PROFILE_ADDING_PROFILE_IMAGE',
	'PROFILE_ADDING_PROFILE_IMAGE_PROGRESS',
	'PROFILE_ADDED_PROFILE_IMAGE',
	'PROFILE_DRAG_OVER_PROFILE_IMAGE',
	'PROFILE_DRAG_OUT_PROFILE_IMAGE',
	'PROFILE_UPDATE_PROVIDER_FIELD',
	'PROFILE_DELETE_IMAGE',
	'PROFILE_HELPER_SKIP',
	'PROFILE_HELPER_CLEAR_SKIPS',
	'PROFILE_ADD_FAVOURITED_COUNT',
	'PROFILE_SUBTRACT_FAVOURITED_COUNT',
	'PROFILE_TOGGLE_YOU_TUBE_PANEL',
	'PROFILE_UPDATE_EMAIL_UNSUB',
	'PROVIDER_SET_IMGS'
);

export const persistProviderField = providerData => (dispatch, getState) => {
	dispatch(actions.profileUpdateProviderField(providerData));

	const {session, profile} = getState();

	const data = {...providerData};
	if (providerData.user && providerData.user.location) {
		// Don't save whole location object, just the suburbUuid, KTHX
		data.user = {
			...data.user,
			suburbUuid: providerData.user.location.suburbUuid
		};
		delete data.user.location;
	}

	if (session && session.current && session.current.provider.uuid === profile.uuid) {
		// Update session subscription level so we don't break things
		dispatch(sessionActions.sessionUpdateCurrentProvider(providerData));
	}

	putJson('/api/providers/' + getState().profile.uuid, data)
		.then(({uniqueName}) => {
			if (
				uniqueName &&
				session &&
				session.current &&
				session.current.provider.uuid === profile.uuid
			) {
				// Update session unique name so we don't break things
				dispatch(sessionActions.sessionUpdateUniqueName(uniqueName));
			}

			// Re-direct if uniqueName changes
			if (uniqueName) {
				dispatch(actions.profileUpdateProviderField({profile: {uniqueName}}));
				dispatch(push('/@' + uniqueName));
			}
		})
		.catch(handleError(dispatch));
};

export const persistContacts = () => (dispatch, getState) => {
	const contacts = getState().profile && getState().profile.editingContacts;

	ReactGA.event({
		category: 'Profile',
		action: 'Edit contacts'
	});

	dispatch(actions.profileCommitContacts(contacts));

	postJson(`/api/user/${getState().profile.user.uuid}/contact`, contacts).catch(
		handleError(dispatch)
	);
};

export const retrieveProfile = uniqueName => (dispatch, getState) => {
	dispatch(actions.profileLoading(uniqueName));

	fetchJson('/api/providers/?profileUniqueName=' + uniqueName)
		.then(provider => {
			dispatch(actions.profileLoaded(provider));
			dispatch(refreshFavourites(provider));
			dispatch(reviewActions.retrieveReviews(provider.uuid));
			dispatch(retrieveAffiliations(provider.user.uuid));
		})
		.catch(handleError(dispatch));
};

export const retrieveOnlyProfile = uniqueName => (dispatch, getState) => {
	dispatch(actions.profileLoading(uniqueName));

	fetchJson('/api/providers/?profileUniqueName=' + uniqueName)
		.then(provider => {
			dispatch(actions.profileLoaded(provider));
		})
		.catch(handleError(dispatch));
};

export const refreshFavourites = provider => (dispatch, getState) => {
	if (!provider) {
		provider = getState().profile;
	}

	if (provider.favouritesPublic) {
		dispatch(retrieveFavourites(provider.uuid));
	} else {
		// If favourites are not public, they can only be retrieved by the user or an admin.
		const currentSession = getState().session.current;
		if (
			currentSession &&
			(currentSession.user.admin || currentSession.provider.uuid === provider.uuid)
		) {
			dispatch(retrieveFavourites(provider.uuid));
		}
	}
};

export const retrieveAffiliations = uuid => (dispatch, getState) => {
	if (!uuid) {
		const profile = getState().profile;
		uuid = profile && profile.user && profile.user.uuid;
	}

	fetchJson('/api/community/?userUuid=' + uuid)
		.then(communities => dispatch(actions.profileAffiliationsLoaded(communities)))
		.catch(handleError(dispatch));
};

export const retrieveFavourites = uuid => dispatch => {
	fetchJson(`/api/providers/${uuid}/favourites/providers`)
		.then(favourites => dispatch(actions.profileFavouritesLoaded(favourites)))
		.catch(handleError(dispatch));
};

export const uploadBackgroundImage = image => (dispatch, getState) => {
	dispatch(actions.profileUpdatingBackgroundImage(image.preview));
	const imageUuid = uuidv4();

	postFile(`/api/providers/${getState().profile.uuid}/image/${imageUuid}`, {image}, prog =>
		dispatch(actions.profileUpdatingBackgroundImageProgress(prog))
	)
		.then(() => dispatch(actions.profileUpdateBackgroundImage(imageUuid)))
		.catch(handleError(dispatch));
};

export const persistBackgroundImage = backgroundImage => (dispatch, getState) => {
	// Set the background image to an existing image.
	dispatch(actions.profileUpdateBackgroundImage(backgroundImage));

	putJson('/api/providers/' + getState().profile.uuid, {
		profile: {backgroundImage}
	}).catch(handleError(dispatch));
};

export const uploadAvatarImage = image => (dispatch, getState) => {
	ReactGA.event({
		category: 'Profile',
		action: 'Upload avatar'
	});

	dispatch(actions.profileUpdatingAvatarImage(image.preview));
	const imageUuid = uuidv4();

	const providerUuid = getState().profile.uuid;

	postFile(`/api/providers/${providerUuid}/image/${imageUuid}?avatar=true`, {image}, prog =>
		dispatch(actions.profileUpdatingAvatarImageProgress(prog))
	)
		.then(() => {
			dispatch(actions.profileUpdateAvatarImage(imageUuid));
			const session = getState().session;
			if (session && session.current && session.current.provider.uuid === providerUuid) {
				// Update session unique name so we don't break things
				dispatch(sessionActions.sessionUpdateAvatarImage(imageUuid));
			}
		})
		.catch(handleError(dispatch));
};

export const persistAvatarImage = avatarImage => (dispatch, getState) => {
	// Set the avatar image to an existing image.
	dispatch(actions.profileUpdateAvatarImage(avatarImage));

	putJson('/api/providers/' + getState().profile.uuid, {
		profile: {avatarImage}
	}).catch(handleError(dispatch));
};

export const persistEmailUnsub = unsub => (dispatch, getState) => {
	// toggle email unsub flag
	const {username, unsubbedFromEmail} = getState().profile.user;
	putJson(`/api/user/${username}/unsubscribe/`, {unsub})
		.then(() => dispatch(actions.profileUpdateEmailUnsub(!unsubbedFromEmail)))
		.catch(handleError(dispatch));
};

export const uploadProfileImage = image => (dispatch, getState) => {
	dispatch(actions.profileAddingProfileImage(image.preview));
	const imageUuid = uuidv4();
	const priority = getState().profile.profile.images.reduce(
		(max, val) => (max > val.priority ? max : val.priority),
		0
	);

	ReactGA.event({
		category: 'Profile',
		action: 'Upload image'
	});

	postFile(
		`/api/providers/${getState().profile.uuid}/image/${imageUuid}?profile=true`,
		{image},
		prog => dispatch(actions.profileAddingProfileImageProgress(prog))
	)
		.then(() =>
			dispatch(actions.profileAddedProfileImage({image: imageUuid, priority: priority + 1}))
		)
		.catch(handleError(dispatch));
};

export const deleteImage = imageUuid => (dispatch, getState) => {
	// Set the avatar image to an existing image.
	dispatch(actions.profileDeleteImage(imageUuid));

	deleteJson(`/api/providers/${getState().profile.uuid}/image/${imageUuid}`).catch(
		handleError(dispatch)
	);
};

export const invite = emails => (dispatch, getState) => {
	ReactGA.event({
		category: 'Profile',
		action: 'Invite others'
	});

	postJson(`/api/user/invite/`, {
		emails
	})
		.then(() => {
			dispatch(uiActions.uiMessage('Email invitations have been sent'));
		})
		.catch(handleError(dispatch));
};

export const moveImage = (imageUuid, forward) => (dispatch, getState) => {
	putJson(`/api/providers/${getState().profile.uuid}/image/${imageUuid}`, {forward})
		.then(imgs => dispatch(actions.providerSetImgs(imgs)))
		.catch(handleError(dispatch));
};

export default {
	...actions,
	retrieveAffiliations,
	retrieveProfile,
	persistProviderField,
	persistContacts,
	uploadBackgroundImage,
	persistBackgroundImage,
	uploadAvatarImage,
	persistAvatarImage,
	deleteImage,
	uploadProfileImage,
	refreshFavourites,
	invite,
	persistEmailUnsub,
	moveImage,
	retrieveOnlyProfile
};



// WEBPACK FOOTER //
// ./src/profile/profile.actions.js